import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://kotomil.com';

export type LocalizedSeo = {
    htmlLang: 'ja' | 'en';
    canonicalUrl: string;
    alternateLinks: { lang: 'ja' | 'en' | 'x-default'; href: string }[];
    ogLocale: 'ja_JP' | 'en_US';
    ogAlternateLocale: 'ja_JP' | 'en_US';
};

const normalizePagePath = (pagePath: string): string => {
    if (!pagePath || pagePath === '/') {
        return '';
    }
    return pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
};

const withLocalePrefix = (locale: 'ja' | 'en', pagePath: string): string => {
    const normalizedPath = normalizePagePath(pagePath);
    return `${BASE_URL}/${locale}${normalizedPath}`;
};

const withoutLocalePrefix = (pagePath: string): string => {
    const normalizedPath = normalizePagePath(pagePath);
    return `${BASE_URL}${normalizedPath || '/'}`;
};

export const getLocalizedSeo = (
    pagePath: string,
    pathname: string,
): LocalizedSeo => {
    const normalizedPath = normalizePagePath(pagePath);
    const pathLocale = pathname.split('/').filter(Boolean)[0];
    const htmlLang = pathLocale === 'ja' ? 'ja' : 'en';
    const jaUrl = withLocalePrefix('ja', normalizedPath);
    const enUrl = withLocalePrefix('en', normalizedPath);
    const defaultUrl = withoutLocalePrefix(normalizedPath);

    const canonicalUrl = pathLocale === 'ja' ? jaUrl : pathLocale === 'en' ? enUrl : defaultUrl;

    return {
        htmlLang,
        canonicalUrl,
        alternateLinks: [
            { lang: 'ja', href: jaUrl },
            { lang: 'en', href: enUrl },
            { lang: 'x-default', href: defaultUrl },
        ],
        ogLocale: htmlLang === 'ja' ? 'ja_JP' : 'en_US',
        ogAlternateLocale: htmlLang === 'ja' ? 'en_US' : 'ja_JP',
    };
};

export const useLocalizedSeo = (pagePath: string): LocalizedSeo => {
    const location = useLocation();
    return getLocalizedSeo(pagePath, location.pathname);
};
